
import moment from 'moment/moment';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import { useDvfYears } from '../../../hooks/useDvfYears';
import { setSelectedDates } from '../../../store/CoproReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import tailwindConfig from '../../../utils/tailwindColors';
import Badge from '../../common/Badge';
import { AdapterMoment, LocalizationProvider } from '../../common/DatePickerCompat';
import { showToast } from '../../common/Notification';
import DismissibleModuleTutorialPopup from '../../common/Tutorials/DismissibleModuleTutorialPopup';
import WarningValidationPending from '../../common/WarningValidationPending';
import type { Filters } from './FiltersBar';
import FiltersBar from './FiltersBar';
import type { CoproRow, PresenceStatus } from './GeneralAssemblyTable';
import GeneralAssemblyTable from './GeneralAssemblyTable';
import PresenceModal from './PresenceModal';
import type { TopStatsData } from './TopStats';
import TopStats from './TopStats';

interface Gestionnaire {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
}

interface CoproData {
  id: number;
  remoteId: string;
  name: string;
  meetingDate: string | null;
  apiMeetingDate: string | null;
  meetingDates?: string[] | null;
  address: string;
  cp: string;
  city: string;
  gestionnaire: Gestionnaire;
  companyId: number;
  batchSize?: number | null;
  boitageQualification?: string | null;
  presences?: Array<{ meetingDate: string; status: PresenceStatus }>;
}

interface SalesStatsItem {
  immatriculation?: string | null;
  salesAmount?: number;
  salesCommission?: number | number[];
  nbSales?: number;
  m2ForNbSales?: number | null;
  salesAvgForM2?: number | null;
}

interface SalesApiResponse {
  results?: SalesStatsItem[];
}

function GeneralAssemblyReferent() {
  const dispatch = useAppDispatch();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMsg, setErrorMsg] = useState<string>();
  const [meetingData, setMeetingData] = useState<CoproData[]>([]);
  const [rows, setRows] = useState<CoproRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<CoproRow[]>([]);
  const [filters, setFilters] = useState<Filters>({ periode: 'all', gestionnaire: 'all', etatDate: 'all', presence: 'all', search: '' });
  const [presenceMap, setPresenceMap] = useState<{ [key: string]: PresenceStatus }>({});
  const [presenceModalOpen, setPresenceModalOpen] = useState(false);
  const [presenceModalRow, setPresenceModalRow] = useState<CoproRow | null>(null);
  const [presenceModalRequiresDate, setPresenceModalRequiresDate] = useState<boolean>(false);
  const [presenceModalSelectedDate, setPresenceModalSelectedDate] = useState<string>('');
  const { yearsOptions } = useDvfYears({ startYear: 2014 });
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [lastSearchedYears, setLastSearchedYears] = useState<number[]>([]);
  const [isFetchingYears, setIsFetchingYears] = useState<boolean>(false);
  const [topStats, setTopStats] = useState<TopStatsData | null>(null);
  const [topStatsLoading, setTopStatsLoading] = useState<boolean>(false);
  function normalizeDateString(input: string): string {
    if (!input) { return ''; }
    try {
      const d = new Date(input);
      if (!isNaN(d.getTime())) {
        const normalized = d.toISOString().split('T')[0];
        return normalized;
      }
    } catch { /* ignore */ }
    if (/^\d{4}-\d{2}-\d{2}/.test(input)) {
      const normalized = input.slice(0, 10);
      return normalized;
    }
    return input;
  }



  function fetchPresenceFor(coproId: number, isoDate: string) {
    fetch(`/api/copros/${coproId}/meeting/presence?meetingDate=${encodeURIComponent(isoDate)}`, JWTHeader(jwt))
      .then(async(r) => {
        if (!r.ok) {throw new Error('presence');}
        return r.json();
      })
      .then((result) => {
        const presenceStatusFromApi = (result && result.status) ? (result.status as PresenceStatus) : 'pending';
        setPresenceMap((prev) => ({ ...prev, [`${coproId}|${isoDate}`]: presenceStatusFromApi }));
      })
      .catch(() => {
        setPresenceMap((prev) => ({ ...prev, [`${coproId}|${isoDate}`]: 'pending' }));
      });
  }

  const handleMeetingDateChange = (coproId: number, isoDate: string, previousPlanned: string | null, lastAg: string | null) => {
    if (!isoDate) {return;}
    const newDates = lastAg ? [lastAg, isoDate] : [isoDate];
    // Delete previous presence first to avoid stale presence on previous planned date
    const prevNorm = normalizeDateString(previousPlanned ?? '');

    // Clear the presence locally immediately, regardless of server response
    if (prevNorm && prevNorm !== isoDate) {
      setPresenceMap((prev) => {
        const newMap = { ...prev };
        delete newMap[`${coproId}|${prevNorm}`];
        return newMap;
      });
    }

    const deletePrevPresence = prevNorm && prevNorm !== isoDate
      ? fetch(`/api/copros/${coproId}/meeting/presence?meetingDate=${encodeURIComponent(prevNorm)}`, { method: 'DELETE', headers: { Authorization: JWTAuthorization(jwt) } })
      : Promise.resolve();
    deletePrevPresence.finally(() => fetch(`/api/copros/${coproId}/general-meeting-dates`, {
        method: 'PUT',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
      body: JSON.stringify({ generalMeetingDates: newDates }),
      })
        .then(async(response) => {
        if (!response.ok) {throw new Error('Error updating meeting dates');}
          showToast('success', 'La date d\'AG a été enregistrée');
          return response.json();
        })
        .then(() => {
        setMeetingData((prevData) => prevData.map((item) => (item.id === coproId ? { ...item, meetingDates: newDates } : item)));
        setRows((prevRows) => prevRows.map((r) => (r.id === coproId ? { ...r, meetingDate: isoDate } : r)));
        const key = `${coproId}|${isoDate}`;
        setPresenceMap((prev) => ({ ...prev, [key]: prev[key] ?? 'pending' }));
        fetchPresenceFor(coproId, isoDate);
        })
        .catch((response_error) => {
          console.error('Error updating copro meeting dates :', response_error);
          showToast('error', 'Une erreur est survenue lors de la mise à jour des données.');
        }));
      };

  const handleLastAgDateChange = (coproId: number, isoDate: string, planned: string | null) => {
    if (!isoDate) { return; }
    const newDates = planned ? [isoDate, planned] : [isoDate];
    fetch(`/api/copros/${coproId}/general-meeting-dates`, {
      method: 'PUT',
      headers: {
        Authorization: JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ generalMeetingDates: newDates }),
    })
      .then(async(response) => {
        if (!response.ok) { throw new Error('Error updating last AG date'); }
        showToast('success', 'La dernière date d\'AG a été enregistrée');
        return response.json();
      })
      .then(() => {
        setMeetingData((prevData) => prevData.map((item) => (item.id === coproId ? { ...item, meetingDates: newDates } : item)));
        setRows((prevRows) => prevRows.map((r) => (r.id === coproId ? { ...r, lastAgDate: isoDate } : r)));
      })
      .catch((err) => {
        console.error('Error updating last AG date:', err);
        showToast('error', 'Impossible de mettre à jour la dernière AG');
      });
  };

  const handleMeetingDateClear = (coproId: number, previousPlanned: string | null, lastAg: string | null) => {
    const newDates = lastAg ? [lastAg] : [];
    const prevNorm = normalizeDateString(previousPlanned ?? '');
    // Clear the presence locally immediately, regardless of server response
    if (prevNorm) {
      setPresenceMap((prev) => {
        const newMap = { ...prev };
        delete newMap[`${coproId}|${prevNorm}`];
        return newMap;
      });
    }

    const deletePrevPresence = prevNorm
      ? fetch(`/api/copros/${coproId}/meeting/presence?meetingDate=${encodeURIComponent(prevNorm)}`, { method: 'DELETE', headers: { Authorization: JWTAuthorization(jwt) } })
      : Promise.resolve();
    deletePrevPresence.finally(() => fetch(`/api/copros/${coproId}/general-meeting-dates`, {
      method: 'PUT',
      headers: {
        Authorization: JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ generalMeetingDates: newDates }),
    })
      .then(async(response) => {
        if (!response.ok) { throw new Error('Error clearing meeting date'); }
        showToast('success', 'La date d\'AG a été supprimée');
        return response.json();
      })
      .then(() => {
        setMeetingData((prevData) => prevData.map((item) => (item.id === coproId ? { ...item, meetingDates: newDates } : item)));
        setRows((prevRows) => prevRows.map((r) => (r.id === coproId ? { ...r, meetingDate: null } : r)));
      })
      .catch((err) => {
        console.error(err);
        showToast('error', 'Impossible de supprimer la date d\'AG');
      }));
  };

  function initializeSelectedDates(uninitedData: CoproData[]) {
    const initialSelectedDates: { [key: number]: string | null } = {};
    uninitedData.forEach((item) => {
      if (item.meetingDate) {
        initialSelectedDates[item.id] = moment(item.meetingDate).format('DD/MM/YYYY');
      } else if (item.apiMeetingDate) {
        initialSelectedDates[item.id] = moment(item.apiMeetingDate).format('DD/MM/YYYY');
      } else {
        initialSelectedDates[item.id] = null;
      }
    });
    dispatch(setSelectedDates(initialSelectedDates));
  }



  const buildRows = (copros: CoproData[], sales: SalesStatsItem[]): CoproRow[] => {
    const salesByImmat = new Map<string, SalesStatsItem>();
    sales.forEach((s) => { if (s.immatriculation) { salesByImmat.set(s.immatriculation, s); } });
    return copros.map((c) => {
      const s = salesByImmat.get(c.remoteId) || {};
      // Normalize provided array of meeting dates (0..2)
      const normalized = (Array.isArray(c.meetingDates) ? c.meetingDates : [])
        .map((d) => { try { const dt = new Date(d); return isNaN(dt.getTime()) ? '' : dt.toISOString().split('T')[0]; } catch { return ''; } })
        .filter((d) => !!d)
        .sort() as string[];
      const today = new Date(); today.setHours(0, 0, 0, 0);
      let lastAgDate: string | null = null;
      for (let i = normalized.length - 1; i >= 0; i -= 1) {
        const d = new Date(normalized[i]);
        if (!isNaN(d.getTime()) && d.getTime() <= today.getTime()) { lastAgDate = normalized[i]; break; }
      }
      const plannedAg: string | null = (() => {
        const future = normalized
          .map((iso) => new Date(iso))
          .filter((d) => !isNaN(d.getTime()) && d.getTime() > today.getTime())
          .sort((a, b) => a.getTime() - b.getTime());
        return future.length > 0 ? future[0].toISOString().split('T')[0] : null;
      })();
      // Forecast from lastAgDate only
      let nextAgForecast: string | null = null;
      if (lastAgDate) {
        const d = new Date(lastAgDate);
        if (!isNaN(d.getTime())) {
          d.setFullYear(d.getFullYear() + 1);
          const month = String(d.getMonth() + 1).padStart(2, '0');
          nextAgForecast = `${d.getFullYear()}-${month}`; // YYYY-MM
        }
      }
      const isoDate = plannedAg;
      const key = isoDate ? `${c.id}|${isoDate}` : '';
      const presence = key ? presenceMap[key] : undefined;
      const gestion = c.gestionnaire && c.gestionnaire.lastname ? `${c.gestionnaire.firstname} ${c.gestionnaire.lastname.toUpperCase()}` : '';
      return {
        id: c.id,
        companyId: c.companyId,
        remoteId: c.remoteId,
        name: c.name,
        address: c.address,
        cp: c.cp,
        city: c.city,
        gestionnaire: gestion,
        nbLots: c.batchSize ?? null,
        salesAmount: s.salesAmount ?? null,
        salesCommission: s.salesCommission ?? null,
        nbSales: s.nbSales ?? null,
        m2ForNbSales: s.m2ForNbSales ?? null,
        salesAvgForM2: s.salesAvgForM2 ?? null,
        meetingDate: plannedAg,
        apiMeetingDate: null,
        lastAgDate,
        nextAgForecast,
        boitageQualification: c.boitageQualification ?? null,
        presence,
      } as CoproRow;
    });
  };

  const fetchMeetingData = () => {
    setStatus('PENDING');
    // Use selected years, or default to last two completed years if none selected
    const years = selectedYears.length > 0
      ? selectedYears
      : (() => { const y = moment().year() - 1; return [y, y - 1]; })();
    setIsFetchingYears(true);
    Promise.all([
      fetch(`/api/companies/${company.id.toString()}/copros/meeting`, JWTHeader(jwt)).then((r) => r.json()),
      fetch(`/api/companies/${company.id.toString()}/stats/copros/sales/years?${years.map((y) => `years[]=${y}`).join('&')}`, JWTHeader(jwt)).then((r) => r.json()),
    ])
      .then(([meetingResult, salesResult]: [CoproData[], SalesApiResponse | SalesStatsItem[]]) => {
        initializeSelectedDates(meetingResult);
        setMeetingData(meetingResult);
        const salesData: SalesStatsItem[] = Array.isArray(salesResult) ? salesResult : (salesResult.results ?? []);
        const newRows = buildRows(meetingResult, salesData);
        setRows(newRows);
        const loadedPresences: { [key: string]: PresenceStatus } = {};
        meetingResult.forEach((copro) => {
          (copro.presences ?? []).forEach(({ meetingDate, status: presenceStatus }) => {
            loadedPresences[`${copro.id}|${meetingDate}`] = presenceStatus;
          });
        });
        setPresenceMap(loadedPresences);
        setLastSearchedYears(years);
        setStatus('SUCCESS');
      })
      .catch((response_error) => {
        console.error('Error fetching company copro meeting datas :', response_error);
        setErrorMsg('Une erreur est survenue lors du chargement des données');
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
        setStatus('ERROR');
      })
      .finally(() => {
        setIsFetchingYears(false);
      });
  };

  useEffect(() => {
    if (!company || !jwt || !company.id) {return;}
    // initial load only
    fetchMeetingData();
     
  }, [jwt, company]);

  // Fetch top stats whenever lastSearchedYears changes (or on initial default selection)
  useEffect(() => {
    const companyId = company?.id;
    if (!companyId || !jwt) {return;}
    if (lastSearchedYears.length === 0) {return;}
    if (topStatsLoading) {return;}

    setTopStatsLoading(true);
    const statsQs = `?${lastSearchedYears.map((y) => `years[]=${y}`).join('&')}`;
    fetch(`/api/companies/${companyId.toString()}/copros/meeting/stats${statsQs}`, { ...JWTHeader(jwt) })
      .then(async(r) => {
        if (!r.ok) { throw new Error('Failed to fetch top stats'); }
        return r.json();
      })
      .then((data: TopStatsData) => setTopStats(data))
      .catch((e) => {
        if (e instanceof Error && e.name === 'AbortError') { return; }
        setTopStats({ totalCopros: 0, coprosWithMoreThan3Sales24m: 0, agThisQuarter: 0 });
      })
      .finally(() => setTopStatsLoading(false));
  }, [jwt, company?.id, lastSearchedYears]);

  useEffect(() => {
    if (yearsOptions.length > 0 && selectedYears.length === 0) {
      setSelectedYears(yearsOptions.slice(0, 2).map((o) => Number(o.value)));
    }
  }, [yearsOptions]);

  useEffect(() => {
    // Rebuild rows when presence updates or meetingData changes
    // Do not refetch sales each time; keep previous stats by merging with current rows data
    setRows((prev) => prev.map((r) => {
      const key = (r.meetingDate || r.apiMeetingDate) ? `${r.id}|${r.meetingDate || r.apiMeetingDate}` : '';
      const presence = key ? presenceMap[key] : r.presence;
      return { ...r, presence };
    }));
  }, [presenceMap, meetingData]);

  useEffect(() => {
    // Apply filters
    const term = filters.search.trim().toLowerCase();
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const currentQuarter = Math.floor(currentMonth / 3);
    const quarterStartMonth = currentQuarter * 3;
    const quarterEndMonth = quarterStartMonth + 2;
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    const result = rows.filter((row) => {
      let show = true;
      // search
      if (term) {
        const hay = `${row.name} ${row.address} ${row.cp} ${row.city} ${row.remoteId}`.toLowerCase();
        if (!hay.includes(term)) { show = false; }
      }
      // periode
      if (show && filters.periode !== 'all') {
        const dateStr = row.meetingDate || row.apiMeetingDate || '';
        if (!dateStr) { show = false; } else {
          const d = new Date(dateStr);
          const m = d.getMonth();
          const y = d.getFullYear();
          if (filters.periode === 'current-month' && (m !== currentMonth || y !== currentYear)) { show = false; }
          if (filters.periode === 'current-quarter' && (m < quarterStartMonth || m > quarterEndMonth || y !== currentYear)) { show = false; }
          if (filters.periode === 'next-month' && (m !== nextMonth || y !== nextYear)) { show = false; }
        }
      }
      // gestionnaire
      if (show && filters.gestionnaire !== 'all' && row.gestionnaire !== filters.gestionnaire) { show = false; }
      // etatDate
      if (show && filters.etatDate !== 'all') {
        const hasDate = !!(row.meetingDate || row.apiMeetingDate);
        if (filters.etatDate === 'qualified' && !hasDate) { show = false; }
        if (filters.etatDate === 'not-qualified' && hasDate) { show = false; }
      }
      // presence
      if (show && filters.presence !== 'all') {
        const p = row.presence || 'pending';
        if (filters.presence !== p) { show = false; }
      }
      return show;
    });
    setFilteredRows(result);
  }, [rows, filters]);

  const gestionnaireOptions = [...new Set(rows.map((r) => r.gestionnaire).filter(Boolean))];

  const openPresence = (row: CoproRow) => {
    setPresenceModalRow(row);
    const isoDate = row.meetingDate || row.apiMeetingDate || '';
    const normalized = normalizeDateString(isoDate);
    setPresenceModalRequiresDate(!normalized);
    setPresenceModalSelectedDate(normalized || '');
    setPresenceModalOpen(true);
  };

  const confirmPresence = (isPresent: boolean) => {
    if (!presenceModalRow) {return;}
    const existingIsoDate = presenceModalRow.meetingDate || presenceModalRow.apiMeetingDate;
    const finalDate = existingIsoDate || presenceModalSelectedDate || '';
    const finalDateNormalized = normalizeDateString(finalDate);
    if (!finalDateNormalized) { setPresenceModalOpen(false); return; }
    const presenceStatus: PresenceStatus = isPresent ? 'confirmed' : 'declined';
    
    const persistPresence = () => {
      fetch(`/api/copros/${presenceModalRow.id}/meeting/presence`, {
        method: 'PUT',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ meetingDate: finalDateNormalized, status: presenceStatus }),
      })
        .then(async(r) => {
          if (!r.ok) {
            const errorText = await r.text();
            console.error('API Error response:', errorText);
            throw new Error('presence');
          }
          return r.json();
        })
        .then(() => {
          setPresenceMap((prev) => ({ ...prev, [`${presenceModalRow.id}|${finalDateNormalized}`]: presenceStatus }));
          showToast('success', 'Présence enregistrée avec succès');
        })
        .catch((error) => {
          console.error('API Error:', error);
          showToast('error', 'Impossible d\'enregistrer la présence');
        })
        .finally(() => setPresenceModalOpen(false));
    };

    // If no date existed, persist the selected date first
    if (!existingIsoDate && presenceModalSelectedDate) {
      // Persist as planned AG while keeping last AG if any
      const row = rows.find((r) => r.id === presenceModalRow.id);
      const lastAg = row?.lastAgDate ?? null;
      const newDates = lastAg ? [lastAg, presenceModalSelectedDate] : [presenceModalSelectedDate];
      fetch(`/api/copros/${presenceModalRow.id}/general-meeting-dates`, {
        method: 'PUT',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ generalMeetingDates: newDates }),
      })
        .then(async(response) => {
          if (!response.ok) { throw new Error('Error updating meeting date'); }
          return response.json();
        })
        .then(() => {
          // Update local state for the row date
          setMeetingData((prevData) => prevData.map((item) => (item.id === presenceModalRow.id ? { ...item, meetingDates: newDates } : item)));
          setRows((prevRows) => prevRows.map((r) => (r.id === presenceModalRow.id ? { ...r, meetingDate: presenceModalSelectedDate } : r)));
          persistPresence();
        })
        .catch((err) => {
          console.error('Error updating meeting date before presence:', err);
          showToast('error', 'Impossible d\'enregistrer la date d\'AG');
          setPresenceModalOpen(false);
        });
    } else {
      persistPresence();
    }
  };

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className="mb-8 rounded-lg bg-white p-4 shadow-md">
        <WarningValidationPending />
        <DismissibleModuleTutorialPopup tutorial="general-assembly-referent" />
        {company?.id && jwt && (
          <TopStats stats={topStats} loading={topStatsLoading || status === 'PENDING'} years={lastSearchedYears} />
        )}
        <FiltersBar
          filters={filters}
          gestionnaireOptions={gestionnaireOptions}
          onChange={setFilters}
          onReset={() => setFilters({ periode: 'all', gestionnaire: 'all', etatDate: 'all', presence: 'all', search: '' })}
          yearsOptions={yearsOptions}
          selectedYears={selectedYears}
          onYearsChange={setSelectedYears}
          onFetchYearsClick={fetchMeetingData}
          canFetchYears={selectedYears.join(',') !== lastSearchedYears.join(',')}
          isFetchingYears={isFetchingYears || status === 'PENDING'}
        />
        
        {/* Legend for row coloring and presence status */}
        {status === 'SUCCESS' && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-700">Légende</h3>
            
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Row coloring legend */}
              <div>
                <h4 className="mb-2 text-xs font-medium text-gray-600">Couleur des lignes (selon le nombre de ventes)</h4>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 rounded border border-gray-200 bg-green-50"></div>
                    <span className="text-xs text-gray-600">10+ ventes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 rounded border border-gray-200 bg-green-100"></div>
                    <span className="text-xs text-gray-600">5-9 ventes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 rounded border border-gray-200 bg-yellow-50"></div>
                    <span className="text-xs text-gray-600">3-4 ventes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="bg-yellow-full h-4 w-4 rounded border border-gray-200"></div>
                    <span className="text-xs text-gray-600">1-2 ventes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 rounded border border-gray-200 bg-red-50"></div>
                    <span className="text-xs text-gray-600">0 vente</span>
                  </div>
                </div>
              </div>
              
              {/* Presence status legend */}
              <div>
                <h4 className="mb-2 text-xs font-medium text-gray-600">Statut de présence</h4>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 rounded" style={{ backgroundColor: '#16a34a' }}></div>
                    <span className="text-xs text-gray-600">Présent</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 rounded" style={{ backgroundColor: '#dc2626' }}></div>
                    <span className="text-xs text-gray-600">Absent</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-4 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
                    <span className="text-xs text-gray-600">À confirmer</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {status === 'PENDING' && (
          <div className="w-full bg-white pb-2 text-center">
            <Skeleton count={6} height={45} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
          </div>
        )}
        {status === 'ERROR'
          && (
            <div className="mx-10 my-32 text-center">
              <Badge
                mode="error"
                text={errorMsg}
              />
            </div>
          )}
        {status === 'SUCCESS' && filteredRows && filteredRows.length > 0 && (
          <div className="overflow-x-auto">
            <GeneralAssemblyTable
              rows={filteredRows}
              isEditable={(r) => r.companyId === company.id}
              onOpenPresence={openPresence}
              onMeetingDateChange={handleMeetingDateChange}
              onMeetingDateClear={handleMeetingDateClear}
              onLastAgDateChange={handleLastAgDateChange}
            />
          </div>
        )}
        {status === 'SUCCESS' && filteredRows && filteredRows.length === 0 && (
          <div className="mx-10 my-10 text-center">
            <Badge mode="warning" text="Aucune copropriété trouvée" />
          </div>
        )}
      </div>
      <PresenceModal
        open={presenceModalOpen}
        propertyName={presenceModalRow?.name || ''}
        onClose={() => setPresenceModalOpen(false)}
        onConfirm={confirmPresence}
        requiresDate={presenceModalRequiresDate}
        selectedDate={presenceModalSelectedDate}
        onChangeDate={setPresenceModalSelectedDate}
      />
      </LocalizationProvider>
    </div>
  );
}

export default GeneralAssemblyReferent;
